import {useFetchEffect} from 'hooks/useFetchEffect'
import {useSelector, useDispatch} from 'react-redux'
import {useHistory, useLocation, useParams} from 'react-router-dom'
import queryString from 'query-string'
import api from 'api'
import {actionCreators, fetchSelector} from 'actions'
import {get, isEmpty, set} from 'lodash'
import {notifySuccess, notifyError} from 'store/actions/notify'
import {asyncError} from 'store/actions/asyncError'
import {RequireEmployeeUpdateRouter} from 'components/router/RequireEmployeeUpdateRouter'
import {RejectUnemployeeRouter} from 'components/router/RejectUnemployeeRouter'
import CommentModal from 'procedures/CommentModal'
import {Form} from 'procedures/changeDependents/remove/Form'
import {makeDependentInitialValues} from './Form'

export const formName = 'changeDependents'

const initialDependentSocialInsurance = (existDependents, removeDependent) =>
  existDependents.find((existDependent) => String(existDependent.id) === removeDependent.id)?.dependent_social_insurance

const initialDependentTaxLaw = (existDependents, removeDependent) =>
  existDependents.find((existDependent) => String(existDependent.id) === removeDependent.id)?.dependent_tax_law

const makeInitialValues = (tmpData, employee, dependentIds) => {
  const dependents = employee.employee_dependents

  const removeDependentsData = tmpData.application_procedure_remove_dependents
  if (removeDependentsData) {
    removeDependentsData.dependents?.map((removeDependent) => ({
      ...removeDependent,
      initial_dependent_social_insurance: initialDependentSocialInsurance(dependents, removeDependent),
      initial_dependent_tax_law: initialDependentTaxLaw(dependents, removeDependent),
    }))

    return removeDependentsData
  }

  return dependents
    ? {
        dependents: dependents
          .filter((dep) => dependentIds.includes(String(dep.id)))
          .map((dependent) => ({
            ...makeDependentInitialValues(dependent),
            initial_dependent_social_insurance: dependent.dependent_social_insurance,
            initial_dependent_tax_law: dependent.dependent_tax_law,
          })),
      }
    : {}
}

export const EmployeeInput = () => {
  const history = useHistory()
  const {search} = useLocation()
  const {id: procedureStatusId} = useParams()
  const {dependentIds} = queryString.parse(search, {arrayFormat: 'bracket'})
  const dispatch = useDispatch()
  const employee = useSelector((state) => state.employees.current.data)
  const clientRoleType = useSelector((state) => state.session.currentUser.client_role.client_role_type)
  const tmpData = useSelector((state) => state.procedureStatuses.tmpData.data)
  const comments = useSelector((state) => fetchSelector(state, 'procedure_comments').data)
  const healthInsuranceType = useSelector((state) =>
    get(state.session.currentUser, 'employee.office.health_insurance_type')
  )
  const token = useSelector((state) => state.auth.token)

  const loadEmployee = () => {
    dispatch(
      actionCreators.employees.current.fetchData(
        api.createWithAuth(token).employees.getCurrent(['employee_dependents', 'office'])
      )
    )
  }
  const loadTmpData = () => {
    dispatch(
      actionCreators.procedureStatuses.tmpData.fetchData(
        api.createWithAuth(token).procedureStatuses.tmpData.mapToJson(procedureStatusId, {
          type: 'application',
          name: 'procedure_remove_dependents',
        })
      )
    )
  }
  const loadComments = () => {
    dispatch(
      actionCreators.fetchData(
        'procedure_comments',
        api.createWithAuth(token).procedureStatuses.procedureComments.list(procedureStatusId)
      )
    )
  }
  const destroy = () => {
    dispatch(actionCreators.employees.current.destroy())
    dispatch(actionCreators.procedureStatuses.tmpData.destroy())
  }
  const updateAndCreateProcedure = async (values, comment) => {
    const authedApi = api.createWithAuth(token)
    let id = procedureStatusId
    try {
      if (!id) {
        const {data} = await authedApi.procedureStatuses.start(
          employee.id,
          'remove_dependents',
          values.date_on,
          'employee_draft',
          false
        )
        id = data.id
      }

      await authedApi.procedureStatuses.tmpData.createOrUpdate(id, values, 'application', 'procedure_remove_dependents')
      await authedApi.procedureStatuses.apply(id, comment)
      history.push('/dashboard')
      dispatch(notifySuccess('申請しました'))
    } catch (err) {
      dispatch(asyncError(err))
    }
  }

  const handleSubmit = async (values, comment) => {
    const {dependents} = values

    if (dependents.some((dep) => dep.removed && (dep.dependent_social_insurance || dep.dependent_tax_law))) {
      dispatch(notifyError('社会保険の扶養、税の扶養の両方とも対象外になっている家族しか削除できません。'))
      return
    }

    await updateAndCreateProcedure(values, comment)
  }

  useFetchEffect(() => {
    loadEmployee()
    loadComments()
    if (procedureStatusId) {
      loadTmpData()
    }

    return () => destroy()
  }, [])

  const validate = (values) => {
    const {dependents, date_on} = values
    if (!dependents) {
      return undefined
    }

    const errors = {dependents: []}

    const needDateOn = dependents.some(
      (dependent) => !dependent.dependent_social_insurance || !dependent.dependent_tax_law
    )
    if (needDateOn && !date_on) {
      errors.date_on = 'を入力してください'
    }

    dependents.forEach((dependent, index) => {
      if (dependent.removed && (dependent.dependent_tax_law || dependent.dependent_social_insurance)) {
        set(
          errors.dependents,
          `[${index}].removed`,
          'は、社会保険の扶養、税の扶養の両方とも対象外になっている家族しか削除できません。'
        )
      }
    })

    return errors.date_on || Object.keys(errors.dependents).length > 0 ? errors : undefined
  }

  return (
    <>
      {!isEmpty(employee) && (
        <RequireEmployeeUpdateRouter employeeId={employee.id} redirectPath="/dashboard?error=employee_info">
          <RejectUnemployeeRouter
            clientRoleType={clientRoleType}
            employmentStatus={employee?.employment_status}
            redirectPath="/dashboard?error=unemployed"
          >
            <CommentModal
              formName={formName}
              procedureType="changeDependents"
              onSubmit={({values, comment}) => handleSubmit(values, comment)}
              comments={comments || []}
            >
              {({showModal}) => (
                <Form
                  onSubmit={showModal}
                  initialValues={makeInitialValues(tmpData, employee, dependentIds)}
                  submitText="申請する"
                  healthInsuranceType={healthInsuranceType}
                  comments={comments || {}}
                  validate={validate}
                />
              )}
            </CommentModal>
          </RejectUnemployeeRouter>
        </RequireEmployeeUpdateRouter>
      )}
    </>
  )
}
